'use client';

import type { AssistantState } from '@assistant-ui/react';
import { useAuiState } from '@assistant-ui/react';
import type { SearchResult } from 'conversational-search-client';

import { extractCitedContentIds } from '../_utils/assetMarkers';
import { useSearchResults } from './useSearchResults';

type ThreadMessages = AssistantState['thread']['messages'];

function latestAssistantText(messages: ThreadMessages): string {
    const lastAssistant = messages.findLast((message) => message.role === 'assistant');
    // Joined with a separator, never '': text parts are distinct blocks, and concatenating
    // them bare could fuse a part ending in an asset code with digits starting the next part
    // into a different (wrong) contentId.
    return (lastAssistant?.parts ?? []).flatMap((part) => (part.type === 'text' ? [part.text] : [])).join('\n');
}

/**
 * Results the LLM cited inline via `SB{V,I,A}-<contentId>` asset codes in its latest message,
 * joined against the already-loaded search results in citation order. Only the latest assistant
 * message counts, so a follow-up answer without citations clears the tray. Cited ids with no
 * matching loaded result are dropped — the tray never triggers extra fetches.
 */
export function useTopResults(): SearchResult[] {
    const messages = useAuiState(({ thread }) => thread.messages);
    const { results } = useSearchResults();
    const citedIds = extractCitedContentIds(latestAssistantText(messages));
    const resultsById = new Map(results.map((result) => [result.contentId, result]));
    return citedIds.flatMap((id) => resultsById.get(id) ?? []);
}
